/**
 * Sessions: everything the org has run, newest first. Filter by outcome,
 * search by person or equipment, open one for the full record.
 *
 * The table is the desktop view. Below md it becomes a list of rows, because
 * eight columns on a phone is either a horizontal scroll nobody finds or type
 * too small to read.
 */
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../api';
import type { SessionRow, SessionStatus } from '../api/types';
import {
  EmptyState,
  ErrorState,
  formatDateTime,
  formatDuration,
  Page,
  PageHeader,
  Panel,
  Segmented,
  SessionStatusBadge,
  SkeletonRows,
  StatusBadge,
} from '../components/patterns';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { go, href } from '../router';

type Filter = 'all' | SessionStatus;

export function Sessions() {
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const listQ = useQuery({
    queryKey: ['sessions', 'list', filter],
    queryFn: () => api.listSessions(filter === 'all' ? {} : { status: filter }),
  });

  const rows = useMemo(() => {
    const all = listQ.data?.sessions ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((s) =>
      [s.technicianName, s.equipmentName, s.procedureName, s.equipmentAssetTag, s.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [listQ.data, search]);

  return (
    <Page>
      <PageHeader
        eyebrow="Sessions"
        title="All sessions"
        lede={listQ.data ? `${rows.length} in this view.` : undefined}
        actions={
          <>
            <div className="relative w-full sm:w-60">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-faint"
                aria-hidden
              />
              <Input
                className="pl-9"
                placeholder="Search person or equipment"
                aria-label="Search sessions"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Segmented
              ariaLabel="Filter by outcome"
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Live' },
                { value: 'completed', label: 'Completed' },
                { value: 'abandoned', label: 'Abandoned' },
                { value: 'failed', label: 'Failed' },
              ]}
            />
          </>
        }
      />

      <Panel flush bodyClassName="min-h-0">
        {listQ.isLoading ? (
          <SkeletonRows rows={6} />
        ) : listQ.error ? (
          <ErrorState error={listQ.error} retry={() => void listQ.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={search ? 'No sessions match' : 'No sessions yet'}
            body={
              search ? 'Try a different name or asset tag.' : 'Completed runs will be listed here.'
            }
          />
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Technician</TableHead>
                    <TableHead>Equipment</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead className="text-right">Duration</TableHead>
                    <TableHead className="text-right">Steps</TableHead>
                    <TableHead className="text-right">Retakes</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Replay</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((s) => (
                    <SessionTr key={s.id} s={s} />
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Phone and small tablet */}
            <ul className="divide-y divide-line md:hidden">
              {rows.map((s) => (
                <li key={s.id}>
                  <a href={href({ name: 'session', id: s.id })} className="block px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-support font-semibold text-ink">
                          {s.technicianName ?? 'Technician'}
                        </div>
                        <div className="truncate text-meta text-ink-dim">
                          {s.equipmentName ?? '—'}
                        </div>
                      </div>
                      <SessionStatusBadge status={s.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-faint">
                      <span>{formatDateTime(s.startedAt)}</span>
                      <span>
                        {s.status === 'active' ? 'running' : formatDuration(s.durationSeconds)}
                      </span>
                      <span>
                        {(s.totalSteps ?? 0) > 0
                          ? `${s.verifiedSteps ?? 0} / ${s.totalSteps} steps`
                          : `${s.verifiedSteps ?? 0} steps`}
                      </span>
                      {(s.retries ?? 0) > 0 ? (
                        <StatusBadge tone="retry">{s.retries} retakes</StatusBadge>
                      ) : null}
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>
    </Page>
  );
}

function SessionTr({ s }: { s: SessionRow }) {
  const total = s.totalSteps ?? 0;
  const done = s.verifiedSteps ?? 0;
  return (
    <TableRow className="cursor-pointer" onClick={() => go({ name: 'session', id: s.id })}>
      <TableCell>
        <div className="font-medium text-ink">{s.technicianName ?? 'Technician'}</div>
        {s.testMode ? <div className="text-meta text-ink-faint">Test run</div> : null}
      </TableCell>
      <TableCell>
        <div className="font-medium text-ink">{s.equipmentName ?? '—'}</div>
        <div className="text-meta text-ink-faint">
          {s.procedureName ?? 'Procedure'} · v{s.procedureVersion}
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap text-ink-dim">{formatDateTime(s.startedAt)}</TableCell>
      <TableCell className="text-right whitespace-nowrap text-ink-dim">
        {s.status === 'active' ? 'running' : formatDuration(s.durationSeconds)}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {total > 0 ? `${done} / ${total}` : done}
      </TableCell>
      <TableCell className="text-right">
        {(s.retries ?? 0) > 0 ? (
          <StatusBadge tone="retry">{s.retries}</StatusBadge>
        ) : (
          <span className="text-ink-faint">0</span>
        )}
      </TableCell>
      <TableCell>
        <SessionStatusBadge status={s.status} />
      </TableCell>
      <TableCell className="text-right">
        <a
          href={href({ name: 'replay', id: s.id })}
          onClick={(e) => e.stopPropagation()}
          data-testid={`session-row-replay-${s.id}`}
          className="font-semibold text-field hover:underline"
        >
          Replay
        </a>
      </TableCell>
    </TableRow>
  );
}
